import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GlobalMessageService, GlobalMessageType, LoggerService, tryNormalizeHttpError } from '@spartacus/core';
import { OrderActions, ReplenishmentOrderHistoryConnector } from '@spartacus/order/core';
import { Order, OrderHistoryList } from '@spartacus/order/root';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { getHttpErrorMessage } from '../../../../../../custom/util/standardize-http-error';
import { OrderStatusList } from '../../../../model/order-status-list.model';
import { CustomUserOrderConnector } from '../../connectors/order/custom-user-order.connector';
import {
  CUSTOM_LOAD_USER_ORDER_DETAILS,
  CUSTOM_LOAD_USER_ORDERS,
  CustomLoadOrderDetails,
  CustomLoadUserOrders,
  LOAD_USER_ORDERS_STATUS,
  LoadUserOrdersStatus,
  LoadUserOrdersStatusFail,
  LoadUserOrdersStatusSuccess
} from '../actions/custom-user-order.action';

@Injectable()
export class CustomUserOrderEffect {
  protected logger = inject(LoggerService);
  customLoadUserOrders$: Observable<OrderActions.UserOrdersAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CUSTOM_LOAD_USER_ORDERS),
      map((action: CustomLoadUserOrders) => action.payload),
      switchMap((payload) => {
        return (
          Boolean(payload.replenishmentOrderCode)
            ? this.replenishmentOrderHistoryConnector.loadReplenishmentDetailsHistory(
                payload.userId,
                payload.replenishmentOrderCode || '',
                payload.pageSize,
                payload.currentPage,
                payload.sort
              )
            : this.customOrderConnector.getHistoryFilter(payload.userId, payload.pageSize, payload.currentPage, payload.sort, payload.ordersFilter)
        ).pipe(
          map((orders: OrderHistoryList) => {
            return new OrderActions.LoadUserOrdersSuccess(orders);
          }),
          catchError((error) => {
            const httpErrorMessage = getHttpErrorMessage(error, this.logger);
            this.globalMessageService.add({ key: `orderHistory.errors.${httpErrorMessage}` }, GlobalMessageType.MSG_TYPE_ERROR);
            return of(new OrderActions.LoadUserOrdersFail({ error: httpErrorMessage }));
          })
        );
      })
    )
  );

  LoadUserOrdersStatus$: Observable<LoadUserOrdersStatusSuccess | LoadUserOrdersStatusFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_USER_ORDERS_STATUS),
      map((action: LoadUserOrdersStatus) => action),
      switchMap(() => {
        return this.customOrderConnector.getOrderStatusList().pipe(
          map((orders: OrderStatusList) => {
            return new LoadUserOrdersStatusSuccess(orders);
          }),
          catchError((error) => {
            const httpErrorMessage = getHttpErrorMessage(error, this.logger);
            this.globalMessageService.add({ key: `orderHistory.errors.${httpErrorMessage}` }, GlobalMessageType.MSG_TYPE_ERROR);
            return of(new LoadUserOrdersStatusFail({ error: httpErrorMessage }));
          })
        );
      })
    )
  );

  loadOrderDetails$: Observable<OrderActions.OrderDetailsAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(CUSTOM_LOAD_USER_ORDER_DETAILS),
      map((action: CustomLoadOrderDetails) => action.payload),
      switchMap((payload) => {
        return this.customOrderConnector.geOrderDetail(payload.userId, payload.orderCode).pipe(
          map((order: Order) => {
            return new OrderActions.LoadOrderDetailsSuccess(order);
          }),
          catchError((error) => of(new OrderActions.LoadOrderDetailsFail(tryNormalizeHttpError(error, this.logger))))
        );
      })
    )
  );

  constructor(
    private actions$: Actions,
    private customOrderConnector: CustomUserOrderConnector,
    private replenishmentOrderHistoryConnector: ReplenishmentOrderHistoryConnector,
    private globalMessageService: GlobalMessageService
  ) {}
}
